import {fallbackCompanyName} from './featureFlagRolloutDirectory';

/**
 * Cohort shape the rollout modal needs — a structural subset of the manage-lists `CohortView`, so
 * this module stays independent of the lists screen while accepting what that screen already builds.
 */
export interface RolloutCohortSource {
	/** Encoded global id. Identifies the picked cohort; nothing is mutated against it here. */
	readonly cohortId: string;
	readonly name: string;
	/** Encoded company ids, in whatever order the backend returned them. */
	readonly memberCompanyIds: readonly string[];
}

/** One row of the cohort picker. */
export interface RolloutCohortOption {
	cohortId: string;
	name: string;
	/** Distinct members, however many of them already hold the flag. */
	memberCount: number;
	/** How many of those members a rollout would skip, because they are already on the flag. */
	alreadyGrantedCount: number;
}

export interface RolloutCohortResolution {
	/** Encoded ids of the companies to add, sorted by company name, each listed once. */
	matchedCompanyIds: string[];
	/** Names of members that already hold the flag, sorted by company name, each listed once. */
	alreadyGrantedNames: string[];
}

export interface RolloutCohortResolutionInput {
	cohort: RolloutCohortSource;
	/** Encoded company id to name — the rollout directory's own map. */
	companyNames: Map<string, string>;
	/**
	 * Every company already on the flag, eligible or not — including one holding it through named
	 * users only. Passing the full set is what keeps a cohort from silently widening a hand-configured
	 * named-users company into a grant for everyone there.
	 */
	grantedCompanyIds: Set<string>;
}

/** Distinct members, in first-seen order. */
function distinctMembers(cohort: RolloutCohortSource): string[] {
	return Array.from(new Set(cohort.memberCompanyIds));
}

/**
 * The picker rows for a set of cohorts, in the order given.
 *
 * Order is preserved rather than sorted here: cohorts arrive already sorted by name, and re-sorting
 * would be a second opinion about an order the manage-lists screen has already settled.
 *
 * A member repeated in the backend's list counts once, so the row's count agrees with how many
 * companies the preview would actually show.
 */
export function buildCohortOptions(
	cohorts: readonly RolloutCohortSource[],
	grantedCompanyIds: Set<string>
): RolloutCohortOption[] {
	return cohorts.map(cohort => {
		const members = distinctMembers(cohort);
		return {
			cohortId: cohort.cohortId,
			name: cohort.name,
			memberCount: members.length,
			alreadyGrantedCount: members.filter(companyId => grantedCompanyIds.has(companyId)).length,
		};
	});
}

/**
 * The companies one cohort would add, split by what can be done with each.
 *
 * Members that already hold the flag are reported separately rather than added again: the server
 * replaces a company's grants by deleting and re-inserting them, so re-sending an unchanged grant
 * would reset that company's "Added" date and lose the record of when it actually got the flag.
 *
 * Members outside the eligible pool — trials and free-forever companies — are added like any other.
 * They simply do not move the rollout percentage. Granting a flag to one deliberately is a legitimate
 * internal action, and the same is true of the CSV path.
 *
 * Both buckets are sorted by company name, case-insensitively, matching how the manage-lists
 * membership table orders the same cohort. A cohort has no order of its own an operator would
 * recognise — unlike a CSV, whose file order mirrors the spreadsheet they are reading from.
 */
export function resolveCohortCompanies({
	cohort,
	companyNames,
	grantedCompanyIds,
}: RolloutCohortResolutionInput): RolloutCohortResolution {
	const named = distinctMembers(cohort)
		.map(companyId => ({companyId, name: companyNames.get(companyId) || fallbackCompanyName(companyId)}))
		.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));

	return {
		matchedCompanyIds: named.filter(member => !grantedCompanyIds.has(member.companyId)).map(member => member.companyId),
		alreadyGrantedNames: named.filter(member => grantedCompanyIds.has(member.companyId)).map(member => member.name),
	};
}
